import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import type { UserRole } from '../lib/permissions/field-permissions';
/**
 * Interfaz del contexto de permisos
 */
interface PermissionsContextType {
  userRole: UserRole;
  setUserRole: (role: UserRole) => void;
}
/**
 * Contexto de permisos
 */
const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);
/**
 * Hook para acceder al contexto de permisos
 */
export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
}
/**
 * Props del provider de permisos
 */
interface PermissionsProviderProps {
  children: ReactNode;
}
/**
 * Provider del contexto de permisos
 * Maneja el estado del rol del usuario actual
 */
export function PermissionsProvider({ children }: PermissionsProviderProps) {
  // Estado del rol del usuario - por defecto 'admin' para desarrollo
  const [userRole, setUserRole] = useState<UserRole>('admin');
  // TODO: Aquí se podría implementar la lógica para obtener el rol del usuario
  // desde autenticación, localStorage, API, etc.
  useEffect(() => {
    // En desarrollo, mantenemos 'admin' por defecto
    // En producción, esto vendría de la autenticación del usuario
    const storedRole = localStorage.getItem('userRole') as UserRole;
    if (storedRole && ['admin', 'manager', 'operator', 'viewer', 'guest'].includes(storedRole)) {
      setUserRole(storedRole);
    }
  }, []);
  // Función para cambiar el rol (útil para testing/debugging)
  const handleSetUserRole = (role: UserRole) => {
    setUserRole(role);
    localStorage.setItem('userRole', role);
  };
  const value: PermissionsContextType = {
    userRole,
    setUserRole: handleSetUserRole,
  };
  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}
